/**
 * Model-finder selection-state plugin — a THREE-version versioned plugin.
 *
 * The client now tracks exactly one value: the deepest selected node id
 * (`selectedId`). But returning visitors already hold the OLD multi-field shape
 * in `localStorage` (`{ manufacturer, deviceType, … }`, each `{ value, name }`,
 * `value` = DB id). To migrate them rather than drop them, the new
 * single-id schema must be registered as **version 2**, not version 1 — because
 * the migration engine short-circuits when `fromVersion === targetVersion`
 * (migrate.ts) and the factory rejects a `migrationToNext` on the latest
 * version. So (SEC-6-1 / spec §3.8):
 *   - **v1** = the legacy multi-field shape, with a real `migrationToNext` that
 *     extracts the deepest non-null `value` as `selectedId` (string → number),
 *     dropping the name path.
 *   - **v2** = `{ selectedId: number | null }` — migrates to v3 unchanged.
 *   - **v3** = `{ selectedId: number | <22-char public id> | null }` — the latest
 *     (W0-4): the `datte` source answers with base64url public-id strings, the
 *     legacy `alt` source keeps (possibly negative) numbers. v2→v3 is the
 *     identity — every stored number is still a valid v3 id.
 *
 * The legacy envelope used `{ version, data }`, not `{ version, value }`; the
 * read-adapter `adaptLegacyEnvelope` renames it so `isVersionedEnvelope`
 * recognises the v1 envelope before the v1→v2 migration runs (see
 * model-finder-state.ts).
 *
 * @see src-js/lib/storage/README.md (versioned-envelope code pattern)
 * @see docs/speed-optimization/research/2026-06-06 - datte-model-finder-endpoints/spec.md §3.8
 * @see ersatzteil-scraper docs/plans/2026-09-15 - interactive-shop-foundation-review/wave-0/W0-4-public-id-urls.md §3.4
 */

import { z } from 'zod';

import { createVersionedPlugin } from '../../versioned/factory';
import type { MigrationFn } from '../../versioned/types';
// The ONE public-id shape rule lives with the NodeId brand; this plugin is
// model-finder-specific, so reaching into its domain module is intended.
import { PUBLIC_ID_PATTERN } from '../../../model-finder/domain/brand';

/** One legacy level cell: `{ value, name }` where `value` is the DB id string. */
const LegacyLevelCell = z
  .object({ value: z.string(), name: z.string() })
  .nullable();

/**
 * v1 — the legacy multi-field selection shape. The levels are ordered shallow →
 * deep; the migration walks them deepest-first to recover the single id.
 * `.passthrough()` tolerates any extra historic keys without dropping the row.
 */
const MfStateV1 = z
  .object({
    manufacturer: LegacyLevelCell,
    deviceType: LegacyLevelCell,
    deviceSubtype: LegacyLevelCell,
    series1: LegacyLevelCell,
    series2: LegacyLevelCell,
    model: LegacyLevelCell,
  })
  .partial()
  .passthrough();

/** v2 — the numeric single-id shape (pre-W0-4). */
export const MfStateV2 = z.object({ selectedId: z.number().nullable() });
export type MfStateV2 = z.infer<typeof MfStateV2>;

/**
 * v3 — the current single-id shape (latest): a legacy numeric id (`alt`, incl.
 * negative virtual series ids) OR a `datte` 22-char base64url public id. Integers
 * only — a stored `1.5` is not a node id and fails validation (→ discarded).
 */
export const MfStateV3 = z.object({
  selectedId: z.union([z.number().int(), z.string().regex(PUBLIC_ID_PATTERN)]).nullable(),
});

/** The current persisted state type (v3). */
export type MfStateV3 = z.infer<typeof MfStateV3>;

/** Shallow → deep level order — the migration reads it in reverse. */
const LEVELS_SHALLOW_TO_DEEP = [
  'manufacturer',
  'deviceType',
  'deviceSubtype',
  'series1',
  'series2',
  'model',
] as const;

/**
 * v1 → v2: take the DEEPEST non-null level's `value`, coerce it to a number,
 * and emit `{ selectedId }`. The name path is dropped (the id is now the SSoT;
 * the path re-derives from the tree's `ancestors` spine). A non-integer or
 * absent value yields `selectedId: null` (empty finder), never `NaN`.
 */
const migrateV1ToV2: MigrationFn = (oldValue) => {
  // Legacy v1 cells only ever held numeric DB ids — public ids postdate v1.
  if (typeof oldValue !== 'object' || oldValue === null) return { selectedId: null };
  const state = oldValue as Record<string, { value?: unknown } | null | undefined>;

  for (let i = LEVELS_SHALLOW_TO_DEEP.length - 1; i >= 0; i--) {
    const cell = state[LEVELS_SHALLOW_TO_DEEP[i]];
    const raw = cell?.value;
    if (typeof raw === 'string' && /^-?\d+$/.test(raw)) {
      return { selectedId: Number(raw) };
    }
  }
  return { selectedId: null };
};

/**
 * v2 → v3: the identity. A v2 `selectedId` is a number (or null), which is
 * already a valid v3 id — numbers carry over unchanged (they keep resolving under
 * the `alt` source; under `datte` the server 404s them = "no seed").
 */
const migrateV2ToV3: MigrationFn = (oldValue) => oldValue;

export const modelFinderStatePlugin = createVersionedPlugin({
  name: 'ModelFinderState',
  versions: [
    { version: 1, schema: MfStateV1, migrationToNext: migrateV1ToV2 },
    { version: 2, schema: MfStateV2, migrationToNext: migrateV2ToV3 },
    { version: 3, schema: MfStateV3 },
  ],
  latestSchema: MfStateV3,
  // 'migrate' (not 'throw'): a stored v1 entry whose deepest value is unusable
  // still resolves to {selectedId:null} via the migration above rather than
  // aborting; the engine never hits a genuinely missing step because v1 carries
  // its migrationToNext.
  onMissingMigration: 'migrate',
});
